import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import VideoCard from '../components/VideoCard';
import { Button } from '../components/ui/button';
import { Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const History = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchHistory();
  }, [isAuthenticated]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/user/history`, getAuthHeader());
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load watch history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="history-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Clock className="w-10 h-10 text-white" />
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Watch History
          </h1>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-history">
            <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-4">No watch history</p>
            <p className="text-gray-600 text-sm mb-6">Videos you watch will appear here</p>
            <Button onClick={() => navigate('/')} data-testid="browse-btn">
              Browse Videos
            </Button>
          </div>
        ) : (
          <>
            <p className="text-gray-400 mb-6">{items.length} videos watched</p>
            <div className="video-grid" data-testid="history-grid">
              {items.map((item, index) => (
                <div key={item.video_id || index} className="relative">
                  <VideoCard 
                    video={{
                      id: item.video_id,
                      title: item.title,
                      thumbnail: item.thumbnail,
                      duration: item.duration,
                      views: formatDate(item.watched_at)
                    }}
                    showProgress={true}
                    progress={item.progress || 0}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default History;
